import { Component, Vue, Prop, Emit, Watch, Inject } from 'vue-property-decorator';
import { eventRepo } from '@/common/repositories/EventRepo';
import { AxiosResponse } from 'axios';
import { ApiErrorResponse } from '@/common/types/AllTypesExports';
import { TicketType } from '@/common/types/event/TicketType';
import { Ticket } from '@/common/types/event/DtoTicket';
import { CouponValidationResult, createCouponValidationResult } from '@/common/types/membership/CouponValidationResult';
import { EventRegistration } from '@/common/types/event/DtoEventRegistration';

@Component({
    name: 'PromoCode',
    components: {}
})
export default class PromoCodeInput extends Vue {

    @Prop({ required: true })
    registration!: EventRegistration;

    @Inject('validator')
    $validator: any;

    validatedDiscountCode: string = "";

    discountCodeInput: string = "";

    applyingDiscount: boolean = false;

    coupon: CouponValidationResult = createCouponValidationResult();

    isPromoInFocus: boolean = false;

    mounted() {
        if (this.registration.promoCode) {
            this.discountCodeInput = this.registration.promoCode;
            this.applyCoupon();
        }
    }

    get discountableTickets() {
        return this.registration.purchasedTickets
            .filter(ticket => ticket.ticketType != TicketType.Donation && this.coupon.discountTickets.includes(ticket.id));
    }

    get discountableProducts() {
        return this.registration.purchasedAddons
            .filter(product => !product.isDonation && this.coupon.discountProducts.includes(product.id));
    }

    get discount() {
        var subTotal = 0;

        if (this.coupon.discountType == 0) {
            var discounts: number[] = [];

            this.processDiscountableTickets(
                this.discountableTickets,
                (ticket) => { discounts.push(ticket.price ?? 0) }
            );

            this.discountableProducts.forEach(product => {
                for (var i = 0; i < product.quantity; i++) {
                    discounts.push(product.price ?? 0);
                }
            });

            if (this.coupon.availableUses != null && this.coupon.availableUses < discounts.length) {
                discounts = discounts.filter(x => x > 0).sort(function (a, b) { return b - a; }).slice(0, this.coupon.availableUses);
            }

            subTotal = discounts.reduce((acc, price) => acc + price, 0);

            return Math.round((subTotal * this.coupon.discountPercent) * 100) / 100
        }

        if (this.coupon.discountType == 1 && this.coupon.discountMethod == 0) {
            subTotal += this.processDiscountableTickets(this.discountableTickets);

            this.discountableProducts.forEach(product => {
                subTotal += ((product.price ?? 0) * product.quantity);
            });

            return Math.min(subTotal, this.coupon.discountAmount);
        }

        if (this.coupon.discountType == 1 && this.coupon.discountMethod == 1) {
            var discounts: number[] = [];

            this.processDiscountableTickets(
                this.discountableTickets,
                (ticket) => { discounts.push(Math.min((ticket.price ?? 0), this.coupon.discountAmount)) }
            );

            this.discountableProducts.forEach(product => {
                for (var i = 0; i < product.quantity; i++) {
                    discounts.push(Math.min((product.price ?? 0), this.coupon.discountAmount));
                }
            });

            if (this.coupon.availableUses != null && this.coupon.availableUses < discounts.length) {
                discounts = discounts.filter(x => x > 0).sort(function (a, b) { return b - a; }).slice(0, this.coupon.availableUses);
            }

            subTotal = discounts.reduce((acc, price) => acc + price, 0);

            return subTotal;
        }

        return 0;
    }

    get taxableDiscount() {
        var subTotal = 0;

        if (this.coupon.discountType == 0) {
            var discounts: any[] = [];

            this.processDiscountableTickets(
                this.discountableTickets,
                (ticket) => { discounts.push({ Price: ticket.price, Taxable: ticket.taxable }) }
            );

            this.discountableProducts.forEach(product => {
                for (var i = 0; i < product.quantity; i++) {
                    discounts.push({ Price: product.price, Taxable: product.taxable });
                }
            });

            if (this.coupon.availableUses != null && this.coupon.availableUses < discounts.length) {
                discounts = discounts.filter(x => x.Price > 0).sort(function (a, b) { return a.Price - b.Price; }).slice(0, this.coupon.availableUses);
            }

            subTotal = discounts.filter(x => x.Taxable).reduce((acc, discount) => acc + discount.Price, 0);

            return Math.round((subTotal * this.coupon.discountPercent) * 100) / 100
        }

        if (this.coupon.discountType == 1 && this.coupon.discountMethod == 0) {
            var discounts: any[] = [];

            subTotal += this.processDiscountableTickets(this.discountableTickets,
                (ticket) => { discounts.push({ Price: Math.min(ticket.price, this.coupon.discountAmount), Taxable: ticket.taxable }) }
            );

            this.discountableProducts.forEach(product => {
                if (product.taxable) {
                    subTotal += (product.price * product.quantity);
                }
            });

            return Math.min(subTotal, this.coupon.discountAmount);
        }

        if (this.coupon.discountType == 1 && this.coupon.discountMethod == 1) {
            var discounts: any[] = [];

            this.processDiscountableTickets(
                this.discountableTickets,
                (ticket) => { discounts.push({ Price: Math.min(ticket.price, this.coupon.discountAmount), Taxable: ticket.taxable }) }
            );

            this.discountableProducts.forEach(product => {
                for (var i = 0; i < product.quantity; i++) {
                    discounts.push({ Price: Math.min(product.price, this.coupon.discountAmount), Taxable: product.taxable });
                }
            });

            if (this.coupon.availableUses != null && this.coupon.availableUses < discounts.length) {
                discounts = discounts.filter(x => x > 0).sort(function (a, b) { return a.Price - b.Price; }).slice(0, this.coupon.availableUses);
            }

            subTotal = discounts.filter(x => x.Taxable).reduce((acc, discount) => acc + discount.Price, 0);

            return subTotal;
        }

        return 0;
    }

    /**
     * Processes an array of tickets, calculates the subtotal of prices with special consideration for multi-seat tickets,
     * and applies an optional discount action for each ticket.
     */
    processDiscountableTickets(tickets: Ticket[], discountAction?: ((ticket: Ticket) => void) | null): number {
        // This is necessary since _each_ seat of a multi-seat ticket will exist as its own ticket in discountableTickets
        // but we don't want to discount each one since the price on each reflects the price of a single ticket (not seat)

        // Map to track the count of remaining seats per multi-seat ticket ID
        let multiSeatTracker = new Map<number, number>();
        let subTotal = 0;
        tickets.forEach(ticket => {
            if (ticket.seatsPerTicket > 1) {
                // Check if the ticket is already in the multiSeatTracker map and if there are remaining seats
                if (multiSeatTracker.has(ticket.id) && multiSeatTracker.get(ticket.id)! > 0) {
                    // Ticket exists in the collection with remaining seats: skip this ticket
                    multiSeatTracker.set(ticket.id, multiSeatTracker.get(ticket.id)! - 1);
                    return; // Skip further processing for this ticket
                } else {
                    // Ticket does not exist in collection or has no remaining seats: track it and process
                    multiSeatTracker.set(ticket.id, ticket.seatsPerTicket - 1);
                }
            }

            // Add ticket price to the subtotal
            subTotal += (ticket.price ?? 0);

            // Apply the discount action if provided
            if (discountAction) {
                discountAction(ticket);
            }
        });

        return subTotal;
    }

    focusPromoInput() {
        this.isPromoInFocus = true;
    }

    applyCoupon() {
        this.clearCoupon();

        this.discountCodeInput = this.discountCodeInput.trim();

        if (!this.discountCodeInput) {
            this.isPromoInFocus = false;
            return;
        }

        this.applyingDiscount = true;

        eventRepo.validateCoupon(this.registration.eventId, this.registration.purchasedAddons.map(x => x.id), this.registration.purchaserId, this.discountCodeInput)
            .then(result => {
                Object.assign(this.coupon, result);

                if (!result.error) {
                    this.validatedDiscountCode = this.discountCodeInput;
                    this.discountCodeInput = "";

                    if ((this.coupon.discountAmount || this.coupon.discountPercent) && !this.discount) {
                        this.coupon.error = `This promo code is not valid for any tickets or add-ons you selected. Please choose eligible tickets/add-ons or remove the promo code to continue checkout.`;
                    }
                }
            })
            .catch((axiosResponse: AxiosResponse<ApiErrorResponse>) => {
                this.coupon.error = `Unable to verify promo code: ${axiosResponse.data.message}`;
                throw axiosResponse;
            })
            .finally(() => {
                this.applyingDiscount = false;
                this.isPromoInFocus = false;
            });
    }

    clearCoupon() {
        this.applyingDiscount = false;
        this.validatedDiscountCode = "";
        this.coupon = createCouponValidationResult();
    }

    @Watch('discount')
    @Emit('update:discount')
    updateDiscount() {
        return this.discount;
    }

    @Watch('taxableDiscount')
    @Emit('update:taxableDiscount')
    updateTaxableDiscount() {
        return this.taxableDiscount;
    }

    @Watch('validatedDiscountCode')
    @Emit('update:discountCode')
    updateDiscountCode() {
        return this.validatedDiscountCode;
    }

    @Watch('coupon.error')
    @Emit('update:discountError')
    updateDiscountError() {
        return this.coupon.error;
    }

    @Watch('isPromoInFocus')
    @Emit('update:isPromoInFocus')
    updatePromoFocus() {
        return this.isPromoInFocus;
    }

    private isEmpty(array: number[]) {
        return !array || array.length === 0;
    }
}